import type { UserFilters } from '../queries/userQueries';
import type { User } from '../queries/userQueries';
import { fetchUsersForUserManagement, userKeys } from '../queries/userQueries';
import { gobodoUserProvider } from '../providers/gobodoUserProvider';
import { roleService } from '../roleService';

/** Stable query key fragment — Admin Users “all users” list (`useUsers` / `fetchUsersForUserManagement` with no filters). */
export const CREDIT_BLOCK_ASSIGNEE_USER_FILTERS: UserFilters = Object.freeze({}) as UserFilters;
export const CREDIT_BLOCK_ROLE_HYDRATED_USER_FILTERS: UserFilters = Object.freeze({ includeRoles: true }) as UserFilters;
export const CREDIT_BLOCK_TEAM_LEAD_TARGET_LABEL = 'Credit Block Team Lead';

/**
 * Single source of truth queryKey for the "active Credit Block team users"
 * list — Team Leads and Processors only. Used by the Team Dashboard, the
 * Unassigned Case Queue Assign modal, the Case Detail owner pickers (inline
 * + Transfer Case modal), and anywhere else that needs the same list.
 *
 * Sharing this key across consumers means React Query dedupes the fetch:
 * one call to fetchCreditBlockTeamUsers per session instead of 3-4.
 */
export const CREDIT_BLOCK_TEAM_USERS_QUERY_KEY = [
  ...userKeys.list(CREDIT_BLOCK_ROLE_HYDRATED_USER_FILTERS),
  'credit-block-team-users',
] as const;

/**
 * Canonical resolver for the active Credit Block team-user list. Used as the
 * queryFn for {@link CREDIT_BLOCK_TEAM_USERS_QUERY_KEY}. Returns active users
 * whose Gobodo roles include Credit Block Team Lead or Processor.
 *
 * Two-step lookup:
 *   1. Pull all active users with hydrated roles and filter to those that
 *      already have a CB team role attached via getUserRoles().
 *   2. If that returns zero (e.g. role_user__c links not yet seeded in this
 *      environment), fall back to fetching the Role records by name and
 *      traversing to their assigned users directly.
 */
export async function fetchCreditBlockTeamUsers(): Promise<User[]> {
  const allActive = (await fetchUsersForUserManagement(CREDIT_BLOCK_ROLE_HYDRATED_USER_FILTERS))
    .filter((user) => user.isActive !== false);
  const fromHydratedRoles = allActive.filter(hasCreditBlockTeamRole);
  if (fromHydratedRoles.length > 0) return fromHydratedRoles;

  const roles = await roleService.getRoles({ includeSystem: true });
  const cbRoles = roles.filter((role) => isCreditBlockTeamRoleName(role.name));
  if (cbRoles.length === 0) return [];
  const roleNamesById = Object.fromEntries(cbRoles.map((role) => [role.id, role.name]));
  const roleUsers = await gobodoUserProvider.getUsersForRoleIds(
    cbRoles.map((role) => role.id),
    roleNamesById,
  );
  return roleUsers.filter((user) => user.isActive !== false);
}

export function encodeNameToken(name: string): string {
  return `n:${encodeURIComponent(name.trim())}`;
}

export function decodeNameToken(value: string): string | null {
  if (!value.startsWith('n:')) return null;
  try {
    return decodeURIComponent(value.slice(2));
  } catch {
    return value.slice(2);
  }
}

export function parseOwnerPick(value: string, options: { value: string; label: string }[]): {
  ownerUserId?: string;
  ownerName: string;
} {
  const opt = options.find((o) => o.value === value);
  const label = opt?.label.replace(/\s*\(you\)\s*$/i, '').trim() ?? '';
  if (value.startsWith('u:')) {
    const id = value.slice(2);
    return { ownerUserId: id, ownerName: label || id };
  }
  const decoded = decodeNameToken(value);
  if (decoded) return { ownerName: decoded };
  return { ownerName: label || value };
}

export function isNameOwnerToken(value: string): boolean {
  return value.startsWith('n:');
}

/** Admin user options only; optional name-only row for current user if missing from list. */
export function ownerSelectOptionsForAssignee(
  userPickerOptions: { value: string; label: string }[],
  currentUserName: string
): { value: string; label: string }[] {
  const byLabel = new Map<string, { value: string; label: string }>();
  for (const u of userPickerOptions) {
    const key = u.label.trim().toLowerCase();
    if (key) byLabel.set(key, u);
  }
  if (currentUserName.trim()) {
    const key = currentUserName.trim().toLowerCase();
    if (!byLabel.has(key)) {
      byLabel.set(key, { value: encodeNameToken(currentUserName.trim()), label: `${currentUserName.trim()} (you)` });
    }
  }
  return Array.from(byLabel.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export function isCreditBlockTeamUser(user: User): boolean {
  const roleNames = [
    String(user.roleName ?? '').trim(),
    ...(Array.isArray(user.roleNames) ? user.roleNames : []).map((r) => String(r ?? '').trim()),
    ...(Array.isArray(user.roles) ? user.roles.map((r) => String(r?.name ?? '').trim()) : []),
  ];
  const tokens = [
    ...roleNames,
    String(user.department ?? '').trim(),
    String(user.title ?? '').trim(),
  ]
    .filter(Boolean)
    .map((v) => v.toLowerCase());

  return tokens.some(
    (v) =>
      v.includes('credit block') ||
      v.includes('creditblock') ||
      v.includes('cb ') ||
      v.startsWith('cb_') ||
      v.includes('credit operations') ||
      v.includes('credit ops')
  );
}

export function isCreditBlockTeamRoleName(roleName: string): boolean {
  const v = String(roleName ?? '').trim().toLowerCase();
  return v === 'credit block team lead' || v === 'credit block processor';
}

export function hasCreditBlockTeamRole(user: User): boolean {
  const roleNames = [
    String(user.roleName ?? '').trim(),
    ...(Array.isArray(user.roleNames) ? user.roleNames : []).map((r) => String(r ?? '').trim()),
    ...(Array.isArray(user.roles) ? user.roles.map((r) => String(r?.name ?? '').trim()) : []),
  ];
  return roleNames.some(isCreditBlockTeamRoleName);
}

export function getCreditBlockRoleLabel(user: User): string {
  const roleNames = [
    String(user.roleName ?? '').trim(),
    ...(Array.isArray(user.roleNames) ? user.roleNames : []).map((r) => String(r ?? '').trim()),
    ...(Array.isArray(user.roles) ? user.roles.map((r) => String(r?.name ?? '').trim()) : []),
  ];
  return Array.from(new Set(roleNames.filter(isCreditBlockTeamRoleName))).join(', ');
}

export function isCreditBlockTeamLeadUser(user: User): boolean {
  const roleNames = [
    String(user.roleName ?? '').trim(),
    ...(Array.isArray(user.roleNames) ? user.roleNames : []).map((r) => String(r ?? '').trim()),
    ...(Array.isArray(user.roles) ? user.roles.map((r) => String(r?.name ?? '').trim()) : []),
  ];
  const roleIds = [
    String(user.roleId ?? '').trim(),
    ...(Array.isArray(user.roleIds) ? user.roleIds : []).map((r) => String(r ?? '').trim()),
    ...(Array.isArray(user.roles) ? user.roles.map((r) => String(r?.id ?? '').trim()) : []),
  ];
  const roleTokens = [...roleNames, ...roleIds]
    .filter(Boolean)
    .map((v) => v.toLowerCase());
  const title = String(user.title ?? '').trim().toLowerCase();

  return (
    roleTokens.some((v) =>
      v === 'credit block team lead' ||
      v === 'cb supervisor' ||
      v === 'cb_supervisor' ||
      v === 'role_cb_lead'
    ) ||
    ((title.includes('credit block') || /\bcb\b/.test(title)) &&
      (title.includes('team lead') || title.includes('supervisor')))
  );
}
